import React, { useState } from 'react';
import { fetchOrderById } from '../../services/order';
import { useAppSelector } from '../../store/hooks/reduxHooks';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Loader2 } from 'lucide-react';

const Row = ({ label, value }: { label: string; value: any }) => (
  <div className="flex justify-between py-1 border-b last:border-b-0">
    <span className="text-gray-600 font-medium">{label}</span>
    <span className="text-gray-900">{value ?? 'N/A'}</span>
  </div>
);

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="bg-white border shadow-sm rounded-lg p-4 mb-6">
    <h3 className="text-lg font-semibold mb-3">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const ManageCancelOrder: React.FC = () => {
  const { token } = useAppSelector(state => state.auth);
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelAmount ,setCancelAmount] = useState(' ');

  const handleFetchOrder = async () => {
    setLoading(true);
    setErrorMsg('');
    setOrder(null);
    setShowCancelForm(false);

    try {
      const fetchedOrder = await fetchOrderById(orderId.trim(), token);

      if (fetchedOrder?.lender?.toLowerCase() !== 'zype') {
        setErrorMsg('❌ This order is not eligible for cancellation. Only Zype orders can be cancelled at the moment.');
        return;
      }

      setOrder(fetchedOrder);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setErrorMsg('❌ Order not found. Please check the Order ID.');
      } else {
        setErrorMsg('🚫 Something went wrong while fetching the order. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProceedClick = () => {
    setShowCancelForm(true);
  };

  const handleSubmitCancellation = () => {
    if (!cancelReason.trim() || !cancelAmount) {
      setErrorMsg('Please enter both : cancellation reason and amount.');
      return;
    }

    // call cancel API here (if exists)
    
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-6">Cancel an Order</h2>

      <div className="flex items-center gap-2 mb-2">
        <Input
          placeholder="Enter Order ID"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
        />
        <Button
          disabled={!orderId.trim() || loading}
          onClick={handleFetchOrder}
          className="whitespace-nowrap"
        >
          {loading ? <Loader2 className="animate-spin h-4 w-4" /> : 'Fetch Order'}
        </Button>
      </div>

      {errorMsg && (
        <div className="flex items-start gap-2 mt-3 text-sm text-red-700 bg-red-100 border border-red-300 rounded-md px-4 py-2">
          <svg className="w-4 h-4 mt-0.5 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 5.5a6.5 6.5 0 11-6.5 6.5A6.5 6.5 0 0112 5.5z" />
          </svg>
          <span>{errorMsg}</span>
        </div>
      )}

      {order && (
        <>
          <Card title="Order Info">
            <Row label="Order ID" value={order.orderId} />
            <Row label="Status" value={order.status} />
            <Row label="Lender" value={order.lender} />
            <Row label="Platform" value={order.platform} />
          </Card>

          <Card title="Customer Info">
            <Row label="Name" value={order.settlement?.customerName || order.customerId?.name} />
            <Row label="Mobile" value={order.settlement?.customerMobileNo || order.customerId?.mobileNo} />
          </Card>

          <Card title="Payment Info">
            <Row label="Order Amount" value={`₹${order.amount || order.settlement.totalAmount}`} />
            <Row label="Scheme Name" value={order.settlement?.schemeName || 'N/A'} />
            <Row label="Monthly EMI Amount" value={`₹${order.settlement?.schemeEMIAmount || 'N/A'}`} />
            <Row label="Interest" value={`₹${order.settlement?.interestAmount || 'N/A'}`} />
          </Card>

          <Card title="Timestamps">
            <Row
              label="Order Created At"
              value={order.orderCreatedAt ? new Date(order.orderCreatedAt).toLocaleString() : 'N/A'}
            />
          </Card>

          {!showCancelForm ? (
            <div className="mt-6">
              <Button variant="danger" onClick={handleProceedClick}>
                Proceed for Cancellation
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <Input
                placeholder="Enter cancellation reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
              <Input
                placeholder="Enter cancellation amount"
                value={cancelAmount}
                onChange={(e) => setCancelAmount(e.target.value)}
              />
              <div className="flex gap-2">
                <Button variant="danger" onClick={handleSubmitCancellation}>
                  Confirm Cancel Order
                </Button>
                <Button variant="outline" onClick={() => setShowCancelForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManageCancelOrder;
